import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinkIcon from '@mui/icons-material/Link';
import ShareIcon from '@mui/icons-material/Share';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SyncIcon from '@mui/icons-material/Sync';
import { DataGrid, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import { IAdminView, useAdminUserViewsQuery } from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import ShareViewsDialog from './ShareViewsDialog';

interface Props {
	userId: number;
	/** Fill the parent (a flex column) instead of using a fixed viewport-based height. */
	fill?: boolean;
}

function ChipList({ items, color }: { items: string[]; color: 'default' | 'primary' | 'secondary' }) {
	if (!items || items.length === 0) {
		return null;
	}
	return (
		<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
			{items.map((item) => (
				<Chip key={item} label={item} size="small" color={color} variant="outlined" />
			))}
		</Stack>
	);
}

export default function UserViewsTable({ userId, fill }: Props) {
	const { data: views, isLoading } = useAdminUserViewsQuery(userId);

	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);

	const selectedViews: IAdminView[] = (views ?? []).filter((v) => selectionModel.includes(v.id));

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			minWidth: 140,
		},
		{
			field: 'display_type',
			headerName: 'Type',
			width: 110,
		},
		{
			field: 'period_lookback',
			headerName: 'Lookback',
			width: 100,
		},
		{
			field: 'created_at',
			headerName: 'Created',
			width: 110,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'last_viewed',
			headerName: 'Last Viewed',
			width: 120,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'inputs',
			headerName: 'Inputs',
			flex: 1.5,
			minWidth: 180,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams) => (
				<ChipList items={params.value as string[]} color="primary" />
			),
		},
		{
			field: 'universe',
			headerName: 'Universe',
			flex: 1.5,
			minWidth: 180,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams) => (
				<ChipList items={params.value as string[]} color="secondary" />
			),
		},
		{
			field: 'shared_from_email',
			headerName: 'Shared From',
			width: 220,
			renderCell: (params: GridRenderCellParams) => {
				if (!params.value) return null;
				return (
					<Stack direction="row" spacing={0.5} alignItems="center">
						{params.row.is_sync_share && (
							<Tooltip title="Auto-synced"><SyncIcon fontSize="small" color="primary" /></Tooltip>
						)}
						{params.row.admin_shared_by_email && (
							<Tooltip title={`Shared by admin: ${params.row.admin_shared_by_email}`}>
								<AdminPanelSettingsIcon fontSize="small" color="warning" />
							</Tooltip>
						)}
						<Typography variant="body2">{params.value}</Typography>
					</Stack>
				);
			},
		},
		{
			field: 'get_link',
			headerName: 'Get Link',
			width: 90,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title="Copy link to clipboard">
					<IconButton
						size="small"
						onClick={() =>
							navigator.clipboard.writeText(
								`https://app.arbinsights.com/view-display/${params.row.id}`
							)
						}
					>
						<LinkIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box sx={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { height: 'calc(100vh - 310px)', minHeight: 300 }}>
			{selectionModel.length > 0 && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
					<Button
						variant="contained"
						size="small"
						startIcon={<ShareIcon />}
						onClick={() => setShareDialogOpen(true)}
					>
						Share {selectionModel.length} View{selectionModel.length !== 1 ? 's' : ''}
					</Button>
				</Box>
			)}
			<DataGrid
				rows={views ?? []}
				columns={columns}
				loading={isLoading}
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectionModel}
				onSelectionModelChange={setSelectionModel}
				components={{ Toolbar: AdminQuickFilterToolbar }}
				density="compact"
				getRowHeight={() => 'auto'}
				sx={[{
					'& .MuiDataGrid-cell': {
						py: 0.75,
						alignItems: 'flex-start',
					},
				}, fill ? { flex: 1, minHeight: 0 } : null]}
			/>
			<ShareViewsDialog
				open={shareDialogOpen}
				onClose={() => {
					setShareDialogOpen(false);
					setSelectionModel([]);
				}}
				views={selectedViews}
				sourceUserId={userId}
			/>
		</Box>
	);
}
